/**
 * Query-key factory. Every key in the app comes from here so invalidation can
 * target a whole subtree by prefix — e.g. invalidating `["location", 12]` also
 * invalidates `["location", 12, "users"]`.
 *
 * Keys replace what `src/state/middleware.ts` did by hand: the listener
 * middleware refetched the merchant list whenever a merchant was created or
 * updated, which is `invalidateQueries({ queryKey: keys.locations.all })`.
 */
export const keys = {
  auth: {
    me: () => ["auth", "me"] as const,
  },

  /** The user's linked locations (GET merchant/linked). */
  locations: {
    all: ["locations"] as const,
    byUser: (userId: number) => ["locations", userId] as const,
    /** Mutation key, so UI outside the form can observe an in-flight create. */
    create: ["locations", "create"] as const,
  },

  /**
   * A single location record (GET merchant/id). Keyed per id, which is what
   * makes switching location tabs instant and structurally prevents a tab from
   * rendering the previously selected location's data.
   */
  location: {
    all: ["location"] as const,
    detail: (id: number) => ["location", id] as const,
    users: (id: number) => ["location", id, "users"] as const,
    marketingMaterial: (id: number) => ["location", id, "marketing-material"] as const,
  },

  /** Reference data, effectively static (long staleTime). */
  reference: {
    availableCountries: () => ["merchant", "available-countries"] as const,
    availableTypes: () => ["merchant", "available-types"] as const,
    pricing: (countryCode?: string, referralCode?: string) =>
      ["pricing", countryCode ?? null, referralCode ?? null] as const,
  },

  /** Per-merchant loyalty data behind the analytics dashboard. */
  loyalty: {
    all: ["loyalty"] as const,
    stats: (merchantId: number) => ["loyalty", "stats", merchantId] as const,
    userStats: (merchantId: number) => ["loyalty", "user-stats", merchantId] as const,
    /**
     * Date-ranged: `RecentStampsTable` passes a window unless the period is
     * "all", so the range belongs in the key — otherwise switching period and
     * back would refetch instead of hitting the cache.
     */
    recentPurchases: (merchantId: number, startDate?: string, endDate?: string) =>
      [
        "loyalty",
        "recent-purchases",
        merchantId,
        { startDate: startDate ?? null, endDate: endDate ?? null },
      ] as const,
    favouriteUsers: (merchantId: number) =>
      ["loyalty", "favourite-users", merchantId] as const,
  },

  flyers: {
    all: ["flyers"] as const,
    byStatus: (status?: string) => ["flyers", "all", status ?? null] as const,
    byMerchant: (merchantId: number) => ["flyers", "byMerchant", merchantId] as const,
  },

  subscriptions: {
    all: ["subscriptions"] as const,
    list: () => ["subscriptions", "all"] as const,
  },

  /**
   * Platform-wide admin stats. The date-ranged entries below are exactly the
   * ones the old `Map` cache in `statsService.ts` could not cache, because its
   * flat string keys had no way to express parameters — so flipping a filter and
   * back always refetched. Putting the params in the key fixes that.
   */
  stats: {
    all: ["stats"] as const,
    overview: () => ["stats", "overview"] as const,
    userGrowth: () => ["stats", "user-growth"] as const,
    inactiveMerchants: () => ["stats", "inactive-merchants"] as const,
    merchants: () => ["stats", "merchants"] as const,
    merchantsForMap: (period: string) => ["stats", "merchants-for-map", period] as const,
    dailyStamps: (startDate?: string, endDate?: string) =>
      ["stats", "daily-stamps", { startDate: startDate ?? null, endDate: endDate ?? null }] as const,
    stampsByStore: (startDate?: string, endDate?: string) =>
      ["stats", "stamps-by-store", { startDate: startDate ?? null, endDate: endDate ?? null }] as const,
    recentStamps: (limit: number, date?: string) =>
      ["stats", "recent-stamps", { limit, date: date ?? null }] as const,
    recentRewards: (limit: number) => ["stats", "recent-rewards", limit] as const,
  },

  admin: {
    uninvoicedPayments: () => ["admin", "uninvoiced-payments"] as const,
  },
} as const;

/**
 * Freshness tiers. Reference data barely changes within a session, so refetching
 * it on navigation is pure waste; admin stats are the opposite.
 */
export const staleTimes = {
  /** Country pricing, available countries/types — stable for a session. */
  reference: 60 * 60_000,
  /** Identity — refetched only when the token changes. */
  session: Infinity,
  /** Admin analytics; matches the TTL the old hand-rolled caches used. */
  stats: 5 * 60_000,
} as const;
